import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import CardWithTitle from 'components/CardWithTitle';
import { FSBTable } from 'components/common/FSBTable';
import { showSequenceNumber } from 'utils/constants';
import { GRADE_FAVOURITED_BY_USERS } from 'pages/MarketListing/MarketListingGraphQL';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setVendorDetails } from 'actions/vendorDetailsAction';

const FavouritedUsersDetail = ({ gradeId }) => {
  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 10,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleVendorOpen = useCallback(
    (row) => {
      const rawTraderId = row?.traderId;
      if (!rawTraderId) return;
      const vendorId = parseInt(rawTraderId, 10);
      if (Number.isNaN(vendorId)) return;
      dispatch(
        setVendorDetails({
          status: true,
          selectedVendor: {
            id: vendorId,
            entityName: row?.entityName || row?.username || '',
          },
        }),
      );
      navigate('/vendors');
    },
    [dispatch, navigate],
  );

  const renderEllipsisCell = (value, onClick) => {
    const parsedValue = value && String(value).trim() ? String(value) : '-';
    if (onClick && parsedValue !== '-') {
      return (
        <span
          className="favourite-users-cell-value text-blue-c5 fw-medium cursor-pointer text-nowrap"
          title={parsedValue}
          onClick={onClick}
          aria-hidden
        >
          {parsedValue}
        </span>
      );
    }

    return (
      <span className="favourite-users-cell-value" title={parsedValue}>
        {parsedValue}
      </span>
    );
  };

  const { data, loading, error } = useQuery(GRADE_FAVOURITED_BY_USERS, {
    variables: {
      gradeId,
      pageNo: filters.pageNo,
      pageSize: filters.pageSize,
    },
    skip: !gradeId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      pageNo: 1,
    }));
  }, [gradeId]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'SNo',
        header: 'S.NO.',
        enableSorting: false,
        size: 220,
        minSize: 220,
        cell: (cell) => showSequenceNumber(cell, filters),
      },
      {
        accessorKey: 'traderId',
        header: 'TRADER ID',
        enableSorting: false,
        size: 220,
        minSize: 220,
        cell: ({ row }) => renderEllipsisCell(row?.original?.traderId),
      },
      {
        accessorKey: 'username',
        header: 'USER NAME',
        enableSorting: false,
        size: 220,
        minSize: 220,
        cell: ({ row }) =>
          renderEllipsisCell(row?.original?.username, () =>
            handleVendorOpen(row?.original),
          ),
      },
      {
        accessorKey: 'entityName',
        header: 'Company Name',
        enableSorting: false,
        size: 220,
        minSize: 220,
        cell: ({ row }) => renderEllipsisCell(row?.original?.entityName),
      },
      {
        accessorKey: 'phoneNumber',
        header: 'PHONE NUMBER',
        enableSorting: false,
        size: 220,
        minSize: 220,
        cell: ({ row }) => renderEllipsisCell(row?.original?.phoneNumber),
      },
    ],
    [filters, handleVendorOpen],
  );

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters((prev) => ({
      ...prev,
      pageNo,
      pageSize,
    }));
  };

  const users = data?.gradeFavouritedByUsers?.users || [];
  const totalCount = data?.gradeFavouritedByUsers?.totalCount || 0;
  const totalPages = data?.gradeFavouritedByUsers?.totalPages || 0;

  return (
    <CardWithTitle title="Grade Favourited By Users">
      {gradeId ? (
        <>
          {error ? (
            <p className="f-12 text-danger mb-2">{error.message}</p>
          ) : null}
          <FSBTable
            data={users}
            totalCount={totalCount}
            totalPages={totalPages}
            columns={columns}
            tableClass="favourite-users-table"
            currentPageNo={filters.pageNo}
            pageSize={filters.pageSize}
            keyField="traderId"
            showSearch={false}
            showFilters={false}
            showDelete={false}
            showPagination
            remote
            loading={loading}
            striped={false}
            hover={false}
            bordered={false}
            wrapperClass="table-responsive word-break"
            handlePageChange={handlePageChange}
          />
        </>
      ) : (
        <p className="f-12 mb-2">Grade not available</p>
      )}
    </CardWithTitle>
  );
};

export default FavouritedUsersDetail;
